import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/axiosInstance';
import {
  ArrowLeftIcon,
  BeakerIcon,
  PlayIcon,
  ClipboardDocumentCheckIcon,
  PhoneIcon,
  StopIcon,
  SpeakerWaveIcon
} from '@heroicons/react/24/outline';
import logger from '../../../utils/logger';

const AITesting = ({ showNotification }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('intent'); // intent, rule, workflow, voiceagent

  // Intent Tester State
  const [intentInput, setIntentInput] = useState('');
  const [intentResult, setIntentResult] = useState(null);
  const [intentLoading, setIntentLoading] = useState(false);

  // Rule Tester State
  const [ruleInput, setRuleInput] = useState('');
  const [ruleResult, setRuleResult] = useState(null);
  const [ruleLoading, setRuleLoading] = useState(false);

  // Workflow Simulator State
  const [workflowId, setWorkflowId] = useState('');
  const [workflowPayload, setWorkflowPayload] = useState('{}');
  const [workflowResult, setWorkflowResult] = useState(null);
  const [workflowLoading, setWorkflowLoading] = useState(false);

  // Voice Agent Tester State
  const [twilioNumbers, setTwilioNumbers] = useState([]);
  const [selectedFromNumber, setSelectedFromNumber] = useState('');
  const [testPhoneNumber, setTestPhoneNumber] = useState('');
  const [callPurpose, setCallPurpose] = useState('support');
  const [customInstructions, setCustomInstructions] = useState('');
  const [voiceAgentLoading, setVoiceAgentLoading] = useState(false);
  const [activeCall, setActiveCall] = useState(null);
  const [callResult, setCallResult] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);
  const [syncedNumbersCount, setSyncedNumbersCount] = useState(0);
  const [syncingNumbers, setSyncingNumbers] = useState(false);

  // Load Twilio numbers on component mount
  useEffect(() => {
    if (activeTab === 'voiceagent') {
      fetchTwilioNumbers();
    }
  }, [activeTab]);

  // Clean up polling interval on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  const fetchTwilioNumbers = async () => {
    try {
      const response = await axiosInstance.get('/api/twilio/numbers');
      if (response.data.success) {
        const activeNumbers = response.data.numbers.filter(n => n.status === 'active');
        setTwilioNumbers(activeNumbers);

        // Count how many are synced with ElevenLabs
        const synced = activeNumbers.filter(n => n.elevenlabsPhoneNumberId).length;
        setSyncedNumbersCount(synced);

        if (activeNumbers.length > 0 && !selectedFromNumber) {
          setSelectedFromNumber(activeNumbers[0].phoneNumber);
        }
      }
    } catch (error) {
      logger.error('Error fetching Twilio numbers:', error);
      showNotification?.('Failed to load Twilio numbers', 'error');
    }
  };

  const syncElevenLabsNumbers = async () => {
    try {
      setSyncingNumbers(true);
      const response = await axiosInstance.post('/api/voice/sync-elevenlabs-numbers');

      if (response.data.success) {
        showNotification?.(`Successfully synced ${response.data.results.synced} phone numbers with ElevenLabs`, 'success');
        // Refresh the numbers list
        await fetchTwilioNumbers();
      }
    } catch (error) {
      logger.error('Error syncing ElevenLabs numbers:', error);
      showNotification?.(error.response?.data?.error || 'Failed to sync phone numbers', 'error');
    } finally {
      setSyncingNumbers(false);
    }
  };

  const initiateTestCall = async () => {
    if (!testPhoneNumber.trim()) {
      showNotification?.('Please enter a phone number to call', 'error');
      return;
    }

    if (!selectedFromNumber) {
      showNotification?.('Please select a Twilio number to call from', 'error');
      return;
    }

    try {
      setVoiceAgentLoading(true);
      setCallResult(null);

      const response = await axiosInstance.post('/api/voice/initiate-call', {
        phoneNumber: testPhoneNumber,
        callPurpose: callPurpose,
        customInstructions: customInstructions || undefined,
        fromNumber: selectedFromNumber
      });

      if (response.data.success) {
        showNotification?.('Test call initiated successfully', 'success');
        setActiveCall({
          callId: response.data.callId,
          callSid: response.data.callSid,
          status: 'initiated',
          startTime: new Date()
        });

        // Start polling for call updates
        startCallPolling(response.data.callId);
      }
    } catch (error) {
      logger.error('Error initiating test call:', error);
      showNotification?.(error.response?.data?.error || 'Failed to initiate test call', 'error');
      setVoiceAgentLoading(false);
    }
  };

  const startCallPolling = (callId) => {
    const interval = setInterval(async () => {
      try {
        const response = await axiosInstance.get(`/api/voice/calls/${callId}`);
        if (response.data.success) {
          const call = response.data.call;

          setActiveCall(prev => ({
            ...prev,
            status: call.status,
            duration: call.duration,
            transcript: call.transcript
          }));

          // Stop polling if call is completed
          if (['completed', 'failed', 'busy', 'no-answer', 'canceled'].includes(call.status)) {
            clearInterval(interval);
            setPollingInterval(null);
            setVoiceAgentLoading(false);
            setCallResult(call);
            setActiveCall(null);
            showNotification?.(`Call ${call.status}`, call.status === 'completed' ? 'success' : 'info');
          }
        }
      } catch (error) {
        logger.error('Error polling call status:', error);
      }
    }, 2000); // Poll every 2 seconds

    setPollingInterval(interval);
  };

  const endTestCall = async () => {
    if (!activeCall) return;

    try {
      await axiosInstance.post(`/api/voice/calls/${activeCall.callId}/end`);
      showNotification?.('Call ended', 'info');

      if (pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }

      setActiveCall(null);
      setVoiceAgentLoading(false);
    } catch (error) {
      logger.error('Error ending call:', error);
      showNotification?.('Failed to end call', 'error');
    }
  };

  const testIntent = async () => {
    if (!intentInput.trim()) {
      showNotification?.('Please enter email content to test', 'error');
      return;
    }

    try {
      setIntentLoading(true);
      const response = await axiosInstance.post('/api/ai/test/intent', {
        content: intentInput
      });
      setIntentResult(response.data.data);
      showNotification?.('Intent classification complete', 'success');
    } catch (error) {
      logger.error('Error testing intent:', error);
      showNotification?.('Failed to test intent', 'error');
      setIntentResult({ error: error.message });
    } finally {
      setIntentLoading(false);
    }
  };

  const testRule = async () => {
    if (!ruleInput.trim()) {
      showNotification?.('Please enter email content to test', 'error');
      return;
    }

    try {
      setRuleLoading(true);
      const response = await axiosInstance.post('/api/ai/test/rule', {
        content: ruleInput
      });
      setRuleResult(response.data.data);
      showNotification?.('Rule matching complete', 'success');
    } catch (error) {
      logger.error('Error testing rule:', error);
      showNotification?.('Failed to test rule', 'error');
      setRuleResult({ error: error.message });
    } finally {
      setRuleLoading(false);
    }
  };

  const simulateWorkflow = async () => {
    if (!workflowId.trim()) {
      showNotification?.('Please enter workflow ID', 'error');
      return;
    }

    try {
      setWorkflowLoading(true);
      const payload = JSON.parse(workflowPayload);
      const response = await axiosInstance.post('/api/ai/test/workflow', {
        workflowId,
        payload
      });
      setWorkflowResult(response.data.data);
      showNotification?.('Workflow simulation complete', 'success');
    } catch (error) {
      logger.error('Error simulating workflow:', error);
      showNotification?.('Failed to simulate workflow', 'error');
      setWorkflowResult({ error: error.message });
    } finally {
      setWorkflowLoading(false);
    }
  };

  const tabs = [
    { id: 'intent', label: 'Intent Tester' },
    { id: 'rule', label: 'Rule Tester' },
    { id: 'workflow', label: 'Workflow Simulator' },
    { id: 'voiceagent', label: 'Voice Agent Test' }
  ];

  const ResultPanel = ({ result, title }) => {
    if (!result) return null;

    return (
      <div className="mt-4 border border-gray-200 dark:border-gray-800 rounded-lg">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-[#1a1a1a]">
          <h3 className="text-sm font-medium text-gray-900 dark:text-white">
            {title}
          </h3>
        </div>
        <div className="p-4">
          <pre className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap font-mono overflow-x-auto">
            {JSON.stringify(result, null, 2)}
          </pre>
        </div>
      </div>
    );
  };

  return (
    <div className="h-full bg-white dark:bg-[#111111] overflow-y-auto">
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-5xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/ai-dashboard')}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                <ArrowLeftIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
              <div>
                <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
                  Testing Playground
                </h1>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Test intents, rules, and workflows before deployment
                </p>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="mt-6 flex gap-2 border-b border-gray-200 dark:border-gray-800">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-gray-900 dark:border-white text-gray-900 dark:text-white'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Intent Tester */}
        {activeTab === 'intent' && (
          <div className="space-y-6">
            <div className="border border-gray-200 dark:border-gray-800 rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800">
                <h2 className="text-lg font-medium text-gray-900 dark:text-white">
                  Intent Classification Tester
                </h2>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Test how Iris classifies email intents
                </p>
              </div>

              <div className="px-6 py-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email Content
                  </label>
                  <textarea
                    value={intentInput}
                    onChange={(e) => setIntentInput(e.target.value)}
                    placeholder="Enter email subject and body to test intent classification..."
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-800 rounded-lg bg-white dark:bg-[#1a1a1a] text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100 focus:border-transparent"
                  />
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={testIntent}
                    disabled={intentLoading}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-medium rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <PlayIcon className="w-4 h-4" />
                    {intentLoading ? 'Testing...' : 'Test Intent'}
                  </button>
                  <button
                    onClick={() => {
                      setIntentInput('');
                      setIntentResult(null);
                    }}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-800 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    Clear
                  </button>
                </div>

                <ResultPanel result={intentResult} title="Classification Result" />
              </div>
            </div>

            {/* Example Intents */}
            <div className="border border-gray-200 dark:border-gray-800 rounded-lg p-6">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                Example Test Cases
              </h3>
              <div className="space-y-2">
                {[
                  'I need access to the marketing folder',
                  'Can you book a meeting room for tomorrow at 2pm?',
                  'Please approve my PTO request for next week',
                  'Invoice #1234 payment confirmation'
                ].map((example, idx) => (
                  <button
                    key={idx}
                    onClick={() => setIntentInput(example)}
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-[#1a1a1a] rounded hover:bg-gray-100 dark:hover:bg-[#222222] transition-colors"
                  >
                    {example}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Rule Tester */}
        {activeTab === 'rule' && (
          <div className="space-y-6">
            <div className="border border-gray-200 dark:border-gray-800 rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800">
                <h2 className="text-lg font-medium text-gray-900 dark:text-white">
                  Rule Matching Tester
                </h2>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Test which automation rules match your email content
                </p>
              </div>

              <div className="px-6 py-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email Content
                  </label>
                  <textarea
                    value={ruleInput}
                    onChange={(e) => setRuleInput(e.target.value)}
                    placeholder="Enter email content to test against automation rules..."
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-800 rounded-lg bg-white dark:bg-[#1a1a1a] text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100 focus:border-transparent"
                  />
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={testRule}
                    disabled={ruleLoading}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-medium rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <PlayIcon className="w-4 h-4" />
                    {ruleLoading ? 'Testing...' : 'Test Rules'}
                  </button>
                  <button
                    onClick={() => {
                      setRuleInput('');
                      setRuleResult(null);
                    }}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-800 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    Clear
                  </button>
                </div>

                <ResultPanel result={ruleResult} title="Matched Rules" />
              </div>
            </div>
          </div>
        )}

        {/* Workflow Simulator */}
        {activeTab === 'workflow' && (
          <div className="space-y-6">
            <div className="border border-gray-200 dark:border-gray-800 rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800">
                <h2 className="text-lg font-medium text-gray-900 dark:text-white">
                  Workflow Simulator
                </h2>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Simulate workflow execution with custom payloads
                </p>
              </div>

              <div className="px-6 py-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Workflow ID
                  </label>
                  <input
                    type="text"
                    value={workflowId}
                    onChange={(e) => setWorkflowId(e.target.value)}
                    placeholder="Enter workflow ID to simulate"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-800 rounded-lg bg-white dark:bg-[#1a1a1a] text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Payload (JSON)
                  </label>
                  <textarea
                    value={workflowPayload}
                    onChange={(e) => setWorkflowPayload(e.target.value)}
                    placeholder='{"sender": "user@example.com", "subject": "Test"}'
                    rows={8}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-800 rounded-lg bg-white dark:bg-[#1a1a1a] text-gray-900 dark:text-white text-sm font-mono focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100 focus:border-transparent"
                  />
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={simulateWorkflow}
                    disabled={workflowLoading}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-medium rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <PlayIcon className="w-4 h-4" />
                    {workflowLoading ? 'Simulating...' : 'Simulate Workflow'}
                  </button>
                  <button
                    onClick={() => {
                      setWorkflowId('');
                      setWorkflowPayload('{}');
                      setWorkflowResult(null);
                    }}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-800 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    Clear
                  </button>
                </div>

                <ResultPanel result={workflowResult} title="Simulation Result" />
              </div>
            </div>
          </div>
        )}

        {/* Voice Agent Test */}
        {activeTab === 'voiceagent' && (
          <div className="space-y-6">
            {/* ElevenLabs Sync Status */}
            {twilioNumbers.length > 0 && syncedNumbersCount === 0 && (
              <div className="border border-yellow-500 dark:border-yellow-400 rounded-lg bg-yellow-50 dark:bg-yellow-900/20">
                <div className="px-6 py-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                        Phone Numbers Not Synced with ElevenLabs
                      </h3>
                      <p className="mt-1 text-sm text-yellow-700 dark:text-yellow-300">
                        Your Twilio numbers need to be synced with ElevenLabs before you can make outbound calls.
                        Click the button below to sync your phone numbers.
                      </p>
                    </div>
                    <button
                      onClick={syncElevenLabsNumbers}
                      disabled={syncingNumbers}
                      className="ml-4 px-4 py-2 bg-yellow-600 text-white text-sm font-medium rounded-lg hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                    >
                      {syncingNumbers ? 'Syncing...' : 'Sync Now'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Test Configuration */}
            <div className="border border-gray-200 dark:border-gray-800 rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800">
                <h2 className="text-lg font-medium text-gray-900 dark:text-white">
                  Voice Agent Test Call
                </h2>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Test AI voice agent with real phone calls (powered by ElevenLabs)
                </p>
              </div>

              <div className="px-6 py-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      From Number (Twilio)
                    </label>
                    <select
                      value={selectedFromNumber}
                      onChange={(e) => setSelectedFromNumber(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-800 rounded-lg bg-white dark:bg-[#1a1a1a] text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100 focus:border-transparent"
                    >
                      <option value="">Select a number</option>
                      {twilioNumbers.map(num => (
                        <option key={num._id} value={num.phoneNumber}>
                          {num.phoneNumber} {num.friendlyName ? `(${num.friendlyName})` : ''}
                        </option>
                      ))}
                    </select>
                    {twilioNumbers.length === 0 && (
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        No Twilio numbers available. Add numbers in{' '}
                        <button
                          onClick={() => navigate('/twilio-management')}
                          className="text-blue-600 hover:underline"
                        >
                          Twilio Management
                        </button>
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      To Number (Test Recipient)
                    </label>
                    <input
                      type="tel"
                      value={testPhoneNumber}
                      onChange={(e) => setTestPhoneNumber(e.target.value)}
                      placeholder="+1 555 123 4567"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-800 rounded-lg bg-white dark:bg-[#1a1a1a] text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Call Purpose
                  </label>
                  <select
                    value={callPurpose}
                    onChange={(e) => setCallPurpose(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-800 rounded-lg bg-white dark:bg-[#1a1a1a] text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100 focus:border-transparent"
                  >
                    <option value="support">Customer Support</option>
                    <option value="sales">Sales Inquiry</option>
                    <option value="follow-up">Follow-up Call</option>
                    <option value="reminder">Reminder/Notification</option>
                    <option value="survey">Survey</option>
                    <option value="general">General</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Custom Instructions (Optional)
                  </label>
                  <textarea
                    value={customInstructions}
                    onChange={(e) => setCustomInstructions(e.target.value)}
                    placeholder="Add any specific instructions for the AI agent (e.g., mention a specific topic, use a certain tone, etc.)"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-800 rounded-lg bg-white dark:bg-[#1a1a1a] text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100 focus:border-transparent"
                  />
                </div>

                <div className="flex gap-2">
                  {!activeCall ? (
                    <>
                      <button
                        onClick={initiateTestCall}
                        disabled={voiceAgentLoading || !testPhoneNumber || !selectedFromNumber}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <PhoneIcon className="w-4 h-4" />
                        {voiceAgentLoading ? 'Initiating...' : 'Start Test Call'}
                      </button>
                      <button
                        onClick={() => {
                          setTestPhoneNumber('');
                          setCallPurpose('support');
                          setCustomInstructions('');
                          setCallResult(null);
                        }}
                        className="px-4 py-2 border border-gray-300 dark:border-gray-800 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                      >
                        Clear
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={endTestCall}
                      className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
                    >
                      <StopIcon className="w-4 h-4" />
                      End Call
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Active Call Monitor */}
            {activeCall && (
              <div className="border border-blue-500 dark:border-blue-400 rounded-lg bg-blue-50 dark:bg-blue-900/20">
                <div className="px-6 py-4 border-b border-blue-200 dark:border-blue-800">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center gap-2">
                      <span className="relative flex h-3 w-3">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                      </span>
                      Call In Progress
                    </h3>
                    <span className="text-sm font-mono text-gray-700 dark:text-gray-300">
                      {Math.floor((new Date() - activeCall.startTime) / 1000)}s
                    </span>
                  </div>
                </div>

                <div className="px-6 py-4 space-y-3">
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Status:</span>
                      <span className="ml-2 font-medium text-gray-900 dark:text-white capitalize">
                        {activeCall.status}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Duration:</span>
                      <span className="ml-2 font-medium text-gray-900 dark:text-white">
                        {activeCall.duration || 0}s
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">To:</span>
                      <span className="ml-2 font-medium text-gray-900 dark:text-white">
                        {testPhoneNumber}
                      </span>
                    </div>
                  </div>

                  {activeCall.transcript && activeCall.transcript.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                        <SpeakerWaveIcon className="w-4 h-4" />
                        Live Transcript
                      </h4>
                      <div className="bg-white dark:bg-[#1a1a1a] rounded border border-gray-200 dark:border-gray-800 p-3 max-h-60 overflow-y-auto space-y-2">
                        {activeCall.transcript.map((segment, idx) => (
                          <div key={idx} className={`text-sm ${segment.speaker === 'agent' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-900 dark:text-white'}`}>
                            <span className="font-medium capitalize">{segment.speaker}:</span>{' '}
                            <span>{segment.text}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Call Results */}
            {callResult && (
              <div className="border border-gray-200 dark:border-gray-800 rounded-lg">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-[#1a1a1a]">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Test Results
                  </h3>
                </div>

                <div className="px-6 py-4 space-y-4">
                  <div className="grid grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Status:</span>
                      <div className="mt-1">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          callResult.status === 'completed'
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                        }`}>
                          {callResult.status}
                        </span>
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Duration:</span>
                      <div className="mt-1 font-medium text-gray-900 dark:text-white">
                        {callResult.duration}s
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Cost:</span>
                      <div className="mt-1 font-medium text-gray-900 dark:text-white">
                        ${(callResult.costUSD || 0).toFixed(4)}
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Sentiment:</span>
                      <div className="mt-1 font-medium text-gray-900 dark:text-white">
                        {callResult.sentimentScore !== undefined ? callResult.sentimentScore.toFixed(2) : 'N/A'}
                      </div>
                    </div>
                  </div>

                  {callResult.transcript && callResult.transcript.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                        Full Transcript
                      </h4>
                      <div className="bg-gray-50 dark:bg-[#1a1a1a] rounded border border-gray-200 dark:border-gray-800 p-4 max-h-96 overflow-y-auto space-y-2">
                        {callResult.transcript.map((segment, idx) => (
                          <div key={idx} className="text-sm">
                            <span className={`font-medium capitalize ${segment.speaker === 'agent' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-900 dark:text-white'}`}>
                              {segment.speaker}:
                            </span>{' '}
                            <span className="text-gray-700 dark:text-gray-300">{segment.text}</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                              ({Math.floor(segment.timestamp / 1000)}s)
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {callResult.recordingUrl && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                        Recording
                      </h4>
                      <audio controls className="w-full">
                        <source src={callResult.recordingUrl} type="audio/mpeg" />
                        Your browser does not support the audio element.
                      </audio>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Help Section */}
            <div className="border border-gray-200 dark:border-gray-800 rounded-lg p-6 bg-gray-50 dark:bg-[#1a1a1a]">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                Testing Tips
              </h3>
              <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                <li>• <strong>First time?</strong> Make sure to sync phone numbers with ElevenLabs using the button above</li>
                <li>• Use your own phone number for safe testing</li>
                <li>• Test different call purposes to see how the agent adapts its personality</li>
                <li>• Try custom instructions to guide the conversation (e.g., "Mention our holiday promotion")</li>
                <li>• Phone numbers must be in E.164 format (+1 555 123 4567)</li>
                <li>• The AI agent has access to your email conversations for context</li>
                <li>• Each test call costs approximately $0.10-0.15 per minute</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AITesting;
